import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { erApiFeil, erNettfeil, importBrukertekst } from '../api/feil.ts';
import {
  useHypoteseConfig,
  useLeggInnFraTidligereKanal,
  useOppfriskConfig,
  type HypothesisConfig,
  type HypothesisImport,
  type HypothesisImportHistory,
  type HypothesisOutcome,
  type ReviewBaselineCheck,
  type ReviewCanFail,
  type ReviewSourceCheck,
} from '../api/useHypotheses.ts';
import { useMe } from '../api/useMe.ts';
import type { Tast } from '../innboks/Tastaturkart.tsx';
import { useKart } from '../skall/kart.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { Brikker, Flate, Fot, Radiogruppe, Tekstknapp } from './deler.tsx';
import { LeddFelt } from './LeddFelt.tsx';
import { navnAv } from './lesning.ts';
import { laasgrunn, porter } from './porter.ts';
import { delsetning, iDagIso, kortDato } from './setning.ts';
import { forLange, LEDD, lesTall, leddFor, tilKropp, TOMT, type LeddNoekkel, type Skjema } from './skjema.ts';

/**
 * «Legg inn fra tidligere kanal» (H2.8, DD-80) på /h/legg-inn, bare for eier. Eieren registrerer en
 * hypotese som ble foreslått før rommet fantes, på vegne av den som foreslo den: tre felt foran
 * (innsenderens jobbadresse, hvor den kom fra, datoen), SAMME ledd og felt som innsendingen
 * (LeddFelt, skjema.ts, porter.ts), og «Historikk» sammenfoldet bak med de fire stegene på den rette
 * stien — vurdert · valgt · startet · konkludert — der hvert steg åpner det neste.
 *
 * Alle leddene står åpne på én side: eieren skriver av en tekst hun har foran seg, og skal lese hver
 * setning, ikke ledes gjennom ett spørsmål om gangen. Setningen står over leddene og følger med.
 *
 * Utkastet lever i komponenten: importen er ETT kall (`POST /hypotheses/import`), så ingen
 * autolagring, intet utkast på tjeneren og ingen `localStorage` (K8). «Legg inn» er låst med grunnen
 * ved knappen til klientens porter er grønne; tjenerens kode etter kallet vises samme sted, oversatt
 * (feil.ts), aldri serverens `message`. Feltfeil vises først når feltet er forlatt.
 */

const TASTER: Tast[] = [
  ['Tab / Shift+Tab', 'neste og forrige felt'],
  ['Ctrl+Enter', 'Legg inn'],
  ['← →', 'flytter valget i retning, kilde, oppdatering og utfall'],
];

const SVAR_MAKS = 500;
const EPOST = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type Steg = 'vurdert' | 'valgt' | 'startet' | 'konkludert';
const STEG: Array<{ k: Steg; navn: string }> = [
  { k: 'vurdert', navn: 'Vurdert' },
  { k: 'valgt', navn: 'Valgt' },
  { k: 'startet', navn: 'Startet' },
  { k: 'konkludert', navn: 'Konkludert' },
];

interface Historikk {
  vurdert: { reviewerId: string; on: string; sjekk: boolean; canFail?: ReviewCanFail; sourceCheck?: ReviewSourceCheck; baselineCheck?: ReviewBaselineCheck; reply: string };
  valgt: { deciderId: string; ownerId: string; on: string; reply: string };
  startet: { on: string; testEndsOn: string; notDoing: string; reply: string };
  konkludert: {
    on: string;
    outcome?: HypothesisOutcome;
    resultValue: string;
    resultMeasuredOn: string;
    learned: string;
    openBlocking: string;
    dataLimitations: string;
    nextSteps: string;
    dissent: string;
    reply: string;
  };
}

const TOM_HISTORIKK: Historikk = {
  vurdert: { reviewerId: '', on: '', sjekk: false, reply: '' },
  valgt: { deciderId: '', ownerId: '', on: '', reply: '' },
  startet: { on: '', testEndsOn: '', notDoing: '', reply: '' },
  konkludert: { on: '', resultValue: '', resultMeasuredOn: '', learned: '', openBlocking: '', dataLimitations: '', nextSteps: '', dissent: '', reply: '' },
};

interface Person {
  id: string;
  navn: string;
}

/** En mangel med feltet den hører til, så feltet kan si det selv (`aria-describedby`). */
interface Feil {
  felt: string;
  tekst: string;
}

/** De tre feltene foran. */
function topplaaser(epost: string, kilde: string, dato: string, iDag: string): Feil[] {
  const ut: Feil[] = [];
  const e = epost.trim();
  if (!EPOST.test(e) || e.length > 254) ut.push({ felt: 'li-epost', tekst: 'Skriv en hel jobbadresse, f.eks. navn@firma.no.' });
  const k = kilde.trim().length;
  if (k < 3 || kilde.length > 140) ut.push({ felt: 'li-kilde', tekst: k < 3 ? 'Skriv hvor den kom fra, minst 3 tegn — f.eks. #hypoteser i Slack.' : `Kort ned «Hvor kom den fra?» med ${kilde.length - 140} tegn.` });
  if (!dato) ut.push({ felt: 'li-foreslaatt', tekst: 'Skriv datoen den ble foreslått.' });
  else if (dato > iDag) ut.push({ felt: 'li-foreslaatt', tekst: 'Datoen kan ikke være i framtiden.' });
  return ut;
}

/**
 * Stegene som er lagt til, i rekkefølge: det steget krever, datoer som ikke går bakover og ikke er i
 * framtiden, og eieren som ikke er beslutningstakeren. Samme regler som tjeneren (HypothesisImport);
 * tjeneren er fortsatt autoriteten.
 */
function steglaaser(h: Historikk, antall: number, foreslaatt: string, iDag: string, min: number): Feil[] {
  const ut: Feil[] = [];
  const krev = (felt: string, ok: boolean, tekst: string) => {
    if (!ok) ut.push({ felt, tekst });
  };
  let forrige: { dato: string; navn: string } = { dato: foreslaatt, navn: 'datoen den ble foreslått' };
  const dato = (felt: string, steg: string, verdi: string) => {
    if (!verdi) return ut.push({ felt, tekst: `«${steg}» mangler datoen.` });
    if (verdi > iDag) return ut.push({ felt, tekst: `«${steg}» kan ikke være i framtiden.` });
    if (forrige.dato && verdi < forrige.dato) ut.push({ felt, tekst: `«${steg}» kan ikke være før ${forrige.navn}.` });
    forrige = { dato: verdi, navn: `«${steg}»` };
    return 0;
  };
  const tekst = (felt: string, verdi: string, minst: number, maks: number, navn: string, valgfri = false) => {
    const t = verdi.trim();
    if (valgfri && !t) return;
    if (t.length < minst) ut.push({ felt, tekst: `«${navn}» må ha minst ${minst} tegn${valgfri ? ', eller stå tom' : ''}.` });
    else if (verdi.length > maks) ut.push({ felt, tekst: `Kort ned «${navn}» med ${verdi.length - maks} tegn.` });
  };
  const svar = (felt: string, verdi: string) => tekst(felt, verdi, 1, SVAR_MAKS, 'Svaret', true);

  if (antall >= 1) {
    const v = h.vurdert;
    krev('li-v-vurderer', Boolean(v.reviewerId), '«Vurdert» mangler vurdereren.');
    dato('li-v-dato', 'Vurdert', v.on);
    if (v.sjekk) krev('li-v-sjekk', Boolean(v.canFail && v.sourceCheck && v.baselineCheck), 'Kvalitetssjekken mangler et svar. Svar på alle tre, eller ta bort sjekken.');
    svar('li-v-svar', v.reply);
  }
  if (antall >= 2) {
    const v = h.valgt;
    krev('li-va-beslutter', Boolean(v.deciderId), '«Valgt» mangler beslutningstakeren.');
    krev('li-va-eier', Boolean(v.ownerId), '«Valgt» mangler eieren av testen.');
    if (v.ownerId && v.ownerId === v.deciderId) ut.push({ felt: 'li-va-eier', tekst: 'Eieren av testen kan ikke være beslutningstakeren.' });
    dato('li-va-dato', 'Valgt', v.on);
    svar('li-va-svar', v.reply);
  }
  if (antall >= 3) {
    const v = h.startet;
    dato('li-s-dato', 'Startet', v.on);
    if (v.testEndsOn && v.on && v.testEndsOn <= v.on) ut.push({ felt: 'li-s-innen', tekst: '«Innen» må være etter datoen testen startet.' });
    tekst('li-s-ikke', v.notDoing, 20, 500, 'Det vi ikke gjør', true);
    svar('li-s-svar', v.reply);
  }
  if (antall >= 4) {
    const v = h.konkludert;
    dato('li-k-dato', 'Konkludert', v.on);
    krev('li-k-utfall', Boolean(v.outcome), '«Konkludert» mangler utfallet.');
    krev('li-k-resultat', lesTall(v.resultValue) !== undefined, v.resultValue.trim() ? 'Resultatet må være et tall.' : '«Konkludert» mangler resultatet.');
    if (!v.resultMeasuredOn) ut.push({ felt: 'li-k-maalt', tekst: '«Konkludert» mangler datoen resultatet ble målt.' });
    else if (v.on && v.resultMeasuredOn > v.on) ut.push({ felt: 'li-k-maalt', tekst: 'Resultatet kan ikke være målt etter at testen ble konkludert.' });
    tekst('li-k-laerte', v.learned, 40, 1000, 'Hva vi lærte');
    tekst('li-k-funn', v.openBlocking, min, 1000, 'Åpne funn');
    tekst('li-k-data', v.dataLimitations, min, 1000, 'Begrensninger i data');
    tekst('li-k-neste', v.nextSteps, 0, 500, 'Neste steg', true);
    tekst('li-k-uenighet', v.dissent, 0, 1000, 'Uenighet', true);
    svar('li-k-svar', v.reply);
  }
  return ut;
}

/** Kroppen til `POST /hypotheses/import`. Tomt og valgfritt utelates; datoene står som «ÅÅÅÅ-MM-DD». */
function tilImport(epost: string, kilde: string, foreslaatt: string, skjema: Skjema, h: Historikk, antall: number): HypothesisImport {
  const form = tilKropp(skjema) as Record<string, unknown>;
  delete form.supersedesId;
  delete form.differenceNote;
  delete form.metricSourceId;
  for (const [k, v] of Object.entries(form)) if (typeof v === 'string' && !v.trim()) delete form[k];
  const kropp: HypothesisImport = { submitter: { email: epost.trim() }, source: kilde.trim(), submittedOn: foreslaatt, ...(form as Partial<HypothesisImport>) };
  const med = <T extends object>(o: T, k: string, v: string) => (v.trim() ? { ...o, [k]: v.trim() } : o);
  const historikk: HypothesisImportHistory = {};
  if (antall >= 1) {
    const v = h.vurdert;
    historikk.review = med({ reviewerId: v.reviewerId, on: v.on, ...(v.sjekk && v.canFail && v.sourceCheck && v.baselineCheck ? { reviewCheck: { canFail: v.canFail, sourceCheck: v.sourceCheck, baselineCheck: v.baselineCheck } } : {}) }, 'reply', v.reply);
  }
  if (antall >= 2) historikk.selection = med({ deciderId: h.valgt.deciderId, ownerId: h.valgt.ownerId, on: h.valgt.on }, 'reply', h.valgt.reply);
  if (antall >= 3) {
    const v = h.startet;
    historikk.start = med(med({ on: v.on, ...(v.testEndsOn ? { testEndsOn: v.testEndsOn } : {}) }, 'notDoing', v.notDoing), 'reply', v.reply);
  }
  if (antall >= 4) {
    const v = h.konkludert;
    historikk.conclusion = med(
      med(med({ on: v.on, outcome: v.outcome!, resultValue: lesTall(v.resultValue)!, resultMeasuredOn: v.resultMeasuredOn, learned: v.learned.trim(), openBlocking: v.openBlocking.trim(), dataLimitations: v.dataLimitations.trim() }, 'nextSteps', v.nextSteps), 'dissent', v.dissent),
      'reply',
      v.reply,
    );
  }
  if (antall > 0) kropp.history = historikk;
  return kropp;
}

/** Vurdereren: behandlere i rommet og eieren selv (HypothesisImportReview). Eieren av testen: alle i rommet og eieren. */
function personer(config: HypothesisConfig | undefined, meg: Person | undefined) {
  const sorter = (a: Person, b: Person) => a.navn.localeCompare(b.navn, 'nb');
  const unik = (liste: Person[]) => [...new Map(liste.map((p) => [p.id, p])).values()].sort(sorter);
  const medlemmer = (config?.members ?? []).map((m) => ({ id: m.id, navn: navnAv(m), rolle: m.roomRole }));
  const megListe = meg ? [meg] : [];
  return {
    vurderere: unik([...medlemmer.filter((m) => m.rolle === 'behandler'), ...megListe]),
    eiere: unik([...medlemmer, ...megListe]),
    beslutningstakere: unik((config?.deciders ?? []).map((d) => ({ id: d.id, navn: d.id === meg?.id ? meg.navn : navnAv(d) }))),
  };
}

export function LeggInnSide() {
  const naviger = useNavigate();
  const me = useMe();
  const config = useHypoteseConfig();
  const oppfriskConfig = useOppfriskConfig();
  const leggInn = useLeggInnFraTidligereKanal();
  const kart = useKart();
  useEffect(() => {
    kart.meldInn('Legg inn', TASTER);
    return () => kart.rydd();
  }, [kart]);

  const [epost, setEpost] = useState('');
  const [kilde, setKilde] = useState('');
  const [foreslaatt, setForeslaatt] = useState('');
  const [skjema, setSkjema] = useState<Skjema>(TOMT);
  const [historikk, setHistorikk] = useState<Historikk>(TOM_HISTORIKK);
  const [antall, setAntall] = useState(0);
  const [beroert, setBeroert] = useState<ReadonlySet<string>>(new Set());
  const [kravEndret, setKravEndret] = useState(false);

  const iDag = iDagIso();
  const min = config.data?.minFieldLength;
  const laster = min === undefined || me.data === undefined;
  const meg: Person | undefined = me.data ? { id: me.data.id, navn: `${navnAv(me.data)} (deg)` } : undefined;
  const folk = useMemo(() => personer(config.data, meg), [config.data, meg?.id, meg?.navn]); // eslint-disable-line react-hooks/exhaustive-deps

  const nullstillFeil = () => {
    if (leggInn.error) leggInn.reset();
    setKravEndret(false);
  };
  const oppdater = (patch: Partial<Skjema>) => {
    setSkjema((s) => ({ ...s, ...patch }));
    nullstillFeil();
  };
  const settSteg = <K extends Steg>(k: K, patch: Partial<Historikk[K]>) => {
    setHistorikk((h) => ({ ...h, [k]: { ...h[k], ...patch } }));
    nullstillFeil();
  };
  const beroer = (felt: string) => setBeroert((b) => (b.has(felt) ? b : new Set(b).add(felt)));

  const topp = topplaaser(epost, kilde, foreslaatt, iDag);
  const mangler = useMemo(() => (min === undefined ? [] : porter(skjema, min)), [skjema, min]);
  const steg = min === undefined ? [] : steglaaser(historikk, antall, foreslaatt, iDag, min);
  const lange = forLange(skjema);
  const feilFor = (felt: string) => (beroert.has(felt) ? (topp.find((f) => f.felt === felt) ?? steg.find((f) => f.felt === felt))?.tekst : undefined);

  const tjenerFeil = leggInn.error;
  const tjenerTekst = tjenerFeil
    ? erNettfeil(tjenerFeil)
      ? 'Fikk ikke lagt inn: uten nett. Skjemaet står, prøv igjen når nettet er tilbake.'
      : erApiFeil(tjenerFeil)
        ? tjenerFeil.code === 'hypothesis_incomplete'
          ? null
          : importBrukertekst(tjenerFeil.code, tjenerFeil.status)
        : 'Fikk ikke lagt inn. Prøv igjen.'
    : null;
  const grunn = laster
    ? 'Henter brukerlista og beslutningstakerne …'
    : topp[0]
      ? topp[0].tekst
      : lange[0]
        ? `Kort ned «${LEDD.find((l) => l.k === leddFor(lange[0]!.felt))!.sp}» med ${lange[0].over} tegn først.`
        : mangler.length > 0
          ? laasgrunn(mangler, { harNavn: true })
          : steg[0]
            ? steg[0].tekst
            : kravEndret
              ? `${me.data?.tenantName ?? 'Leietakeren'} har endret kravene. Se over leddene og prøv igjen.`
              : tjenerTekst;

  const send = () => {
    if (grunn || leggInn.isPending) return;
    leggInn.mutate(tilImport(epost, kilde, foreslaatt, skjema, historikk, antall), {
      onSuccess: (h) => void naviger(h.number !== undefined ? `/h/registeret/${h.number}` : '/h/registeret'),
      onError: (e) => {
        // Minimumet kan være hevet siden siden ble åpnet: portene kjøres på nytt med det nye.
        if (erApiFeil(e) && e.code === 'hypothesis_incomplete') void oppfriskConfig().then(() => setKravEndret(true));
      },
    });
  };

  const deler = delsetning(skjema, {});
  const aapne = (ledd: LeddNoekkel) => document.getElementById(`li-ledd-${ledd}`)?.querySelector<HTMLElement>('input, textarea, [role="radio"][tabindex="0"]')?.focus();

  const fot = (
    <Fot
      venstre={
        <Tekstknapp onClick={() => void naviger('/h/registeret')} testid="li-lukk">
          Lukk
        </Tekstknapp>
      }
      grunn={grunn}
      primar={{ label: leggInn.isPending ? 'Legger inn …' : 'Legg inn', onClick: send, opptatt: leggInn.isPending, ikon: 'add-circle-outline', testid: 'li-legg-inn' }}
    />
  );

  const lagtTil = STEG.slice(0, antall);
  const neste = STEG[antall];

  return (
    <Flate tittel="Legg inn fra tidligere kanal" tilbake={{ navn: 'Registeret', til: '/h/registeret' }} fot={fot} bredde="skjema">
      <div className="li-skjema" data-testid="li-skjema">
        <p className="h-hjelp">Du legger inn hypotesen på vegne av den som foreslo den. Hun står som innsender, med datoen den ble foreslått.</p>
        <fieldset className="li-gruppe" disabled={laster} aria-busy={laster || undefined}>
          <legend className="panel-skjult">Hvor hypotesen kom fra</legend>
          <div className="li-topp">
            <LiFelt id="li-epost" etikett="Innsenderens jobbadresse" grunn={feilFor('li-epost')}>
              <input id="li-epost" type="email" autoComplete="off" className="h-input" value={epost} {...feltAttr('li-epost', feilFor('li-epost'))} onChange={(e) => (setEpost(e.target.value), nullstillFeil())} onBlur={() => beroer('li-epost')} />
            </LiFelt>
            <LiFelt id="li-kilde" etikett="Hvor kom den fra?" grunn={feilFor('li-kilde')}>
              <input id="li-kilde" className="h-input" placeholder="#hypoteser i Slack" value={kilde} {...feltAttr('li-kilde', feilFor('li-kilde'))} onChange={(e) => (setKilde(e.target.value), nullstillFeil())} onBlur={() => beroer('li-kilde')} />
            </LiFelt>
            <LiFelt id="li-foreslaatt" etikett="Foreslått den" grunn={feilFor('li-foreslaatt')}>
              <input id="li-foreslaatt" type="date" max={iDag} className="h-input h-input-dato" value={foreslaatt} {...feltAttr('li-foreslaatt', feilFor('li-foreslaatt'))} onChange={(e) => (setForeslaatt(e.target.value), nullstillFeil())} onBlur={() => beroer('li-foreslaatt')} />
            </LiFelt>
          </div>
        </fieldset>

        <div className="h-setning-boks li-setning">
          <p className={skjema.title.trim() ? 'h-setning-tittel' : 'h-setning-tittel h-setning-tittel-tom'}>{skjema.title.trim() || 'Uten tittel'}</p>
          <Brikker deler={deler} str="h3" onAapne={aapne} />
        </div>

        <fieldset className="li-gruppe" disabled={laster}>
          <legend className="panel-skjult">Hypotesen</legend>
          <ol aria-label="Leddene" className="h-ledd li-ledd">
            {LEDD.map((l) => (
              <li key={l.k} id={`li-ledd-${l.k}`} className="h-ledd-aapent" data-testid={`li-ledd-${l.k}`}>
                <h2 className="h-sporsmal" id={`sp-${l.k}`}>
                  {l.sp}
                  {l.valgfri ? <span className="hb-liten"> · valgfritt</span> : null}
                </h2>
                <LeddFelt k={l.k} s={skjema} oppdater={oppdater} spId={`sp-${l.k}`} />
              </li>
            ))}
          </ol>
        </fieldset>

        <details className="li-historikk" data-testid="li-historikk">
          <summary className="li-historikk-tittel">
            <Ikon navn="time-outline" />
            Historikk
            <span className="hb-liten">{antall === 0 ? ' · ingen steg — den legges inn som mottatt' : ` · ${lagtTil.map((s) => s.navn.toLowerCase()).join(' · ')}`}</span>
          </summary>
          <p className="h-hjelp">Var den alt vurdert, valgt, startet eller konkludert? Legg til stegene i rekkefølge, med datoen og den som avgjorde. Avvist, parkert og stoppet settes etterpå på vanlig måte.</p>
          {lagtTil.map((s, i) => (
            <fieldset key={s.k} className="li-steg" aria-label={s.navn} onBlur={(e) => e.target.id && beroer(e.target.id)} data-testid={`li-steg-${s.k}`}>
              <legend className="li-steg-tittel">{s.navn}</legend>
              {s.k === 'vurdert' ? (
                <StegVurdert v={historikk.vurdert} sett={(p) => settSteg('vurdert', p)} folk={folk.vurderere} feilFor={feilFor} iDag={iDag} />
              ) : s.k === 'valgt' ? (
                <StegValgt v={historikk.valgt} sett={(p) => settSteg('valgt', p)} beslutningstakere={folk.beslutningstakere} eiere={folk.eiere} feilFor={feilFor} iDag={iDag} />
              ) : s.k === 'startet' ? (
                <StegStartet v={historikk.startet} sett={(p) => settSteg('startet', p)} feilFor={feilFor} iDag={iDag} />
              ) : (
                <StegKonkludert v={historikk.konkludert} sett={(p) => settSteg('konkludert', p)} feilFor={feilFor} iDag={iDag} />
              )}
              {i === antall - 1 ? (
                <div>
                  <button type="button" className="panel-knapp-lenke" onClick={() => (setAntall(i), nullstillFeil())}>
                    Fjern «{s.navn}»
                  </button>
                </div>
              ) : null}
            </fieldset>
          ))}
          {neste ? (
            <div>
              <button
                type="button"
                className="panel-knapp-sekundaer"
                aria-disabled={laster || undefined}
                onClick={
                  laster
                    ? undefined
                    : () => {
                        // «Startet»: «innen»-datoen fra formen er utgangspunktet, som ved start.
                        if (neste.k === 'startet' && !historikk.startet.testEndsOn && skjema.testEndsOn) settSteg('startet', { testEndsOn: skjema.testEndsOn });
                        setAntall(antall + 1);
                        nullstillFeil();
                      }
                }
              >
                <Ikon navn="add-outline" />
                Legg til «{neste.navn}»
              </button>
            </div>
          ) : null}
        </details>
      </div>
    </Flate>
  );
}

/** `aria-invalid` og `aria-describedby` først når feltet er forlatt med noe galt. */
function feltAttr(id: string, grunn: string | undefined) {
  return grunn ? { 'aria-invalid': true as const, 'aria-describedby': `${id}-grunn`, 'data-testid': id } : { 'data-testid': id };
}

function LiFelt({ id, etikett, grunn, children, hint }: { id: string; etikett: string; grunn?: string; children: ReactNode; hint?: ReactNode }) {
  return (
    <div className="h-felt">
      <label htmlFor={id} className="h-etikett">
        {etikett}
      </label>
      {children}
      {hint}
      {grunn ? (
        <span className="h-felt-grunn" id={`${id}-grunn`}>
          <Ikon navn="warning-outline" className="h-varsel-ikon-warning" />
          <span>{grunn}</span>
        </span>
      ) : null}
    </div>
  );
}

type FeilFor = (felt: string) => string | undefined;

function Velg({ id, etikett, verdi, folk, onVelg, feilFor }: { id: string; etikett: string; verdi: string; folk: Person[]; onVelg: (id: string) => void; feilFor: FeilFor }) {
  const grunn = feilFor(id);
  return (
    <LiFelt id={id} etikett={etikett} grunn={grunn}>
      <select id={id} className="h-input" value={verdi} {...feltAttr(id, grunn)} onChange={(e) => onVelg(e.target.value)}>
        <option value="">Velg …</option>
        {folk.map((p) => (
          <option key={p.id} value={p.id}>
            {p.navn}
          </option>
        ))}
      </select>
    </LiFelt>
  );
}

function Dato({ id, etikett, verdi, onEndre, feilFor, iDag }: { id: string; etikett: string; verdi: string; onEndre: (v: string) => void; feilFor: FeilFor; iDag?: string }) {
  const grunn = feilFor(id);
  return (
    <LiFelt id={id} etikett={etikett} grunn={grunn}>
      <input id={id} type="date" max={iDag} className="h-input h-input-dato" value={verdi} {...feltAttr(id, grunn)} onChange={(e) => onEndre(e.target.value)} />
    </LiFelt>
  );
}

function Tekst({ id, etikett, verdi, onEndre, feilFor, rader = 3, maks }: { id: string; etikett: string; verdi: string; onEndre: (v: string) => void; feilFor: FeilFor; rader?: number; maks: number }) {
  const grunn = feilFor(id);
  const vis = verdi.length >= maks * 0.8;
  return (
    <LiFelt id={id} etikett={etikett} grunn={grunn} hint={vis ? <span className={verdi.length > maks ? 'h-teller h-teller-over' : 'h-teller'}>{`${verdi.length} / ${maks}`}</span> : null}>
      <textarea id={id} className="h-input h-tekstfelt" rows={rader} value={verdi} {...feltAttr(id, grunn)} onChange={(e) => onEndre(e.target.value)} />
    </LiFelt>
  );
}

function Svar({ id, verdi, onEndre, feilFor }: { id: string; verdi: string; onEndre: (v: string) => void; feilFor: FeilFor }) {
  return <Tekst id={id} etikett="Svaret · valgfritt" verdi={verdi} onEndre={onEndre} feilFor={feilFor} rader={2} maks={SVAR_MAKS} />;
}

function StegVurdert({ v, sett, folk, feilFor, iDag }: { v: Historikk['vurdert']; sett: (p: Partial<Historikk['vurdert']>) => void; folk: Person[]; feilFor: FeilFor; iDag: string }) {
  const sjekkGrunn = feilFor('li-v-sjekk');
  return (
    <>
      <div className="h-felter-rad">
        <Velg id="li-v-vurderer" etikett="Vurderer" verdi={v.reviewerId} folk={folk} onVelg={(id) => sett({ reviewerId: id })} feilFor={feilFor} />
        <Dato id="li-v-dato" etikett="Vurdert den" verdi={v.on} onEndre={(d) => sett({ on: d })} feilFor={feilFor} iDag={iDag} />
      </div>
      <label className="li-avkrysning">
        <input type="checkbox" id="li-v-sjekk" checked={v.sjekk} onChange={(e) => sett({ sjekk: e.target.checked })} aria-describedby={sjekkGrunn ? 'li-v-sjekk-grunn' : undefined} />
        Kvalitetssjekken ble gjort
      </label>
      {v.sjekk ? (
        <div className="h-felter-kolonne">
          <div className="h-felt">
            <span id="li-v-kanbomme" className="h-etikett">
              Kan den bomme?
            </span>
            <Radiogruppe etikettId="li-v-kanbomme" valg={[['ja', 'Ja'], ['nei', 'Nei']]} aktiv={v.canFail} onVelg={(x) => sett({ canFail: x })} />
          </div>
          <div className="h-felt">
            <span id="li-v-kildesjekk" className="h-etikett">
              Kilden
            </span>
            <Radiogruppe etikettId="li-v-kildesjekk" valg={[['ja', 'Finnes og er definert'], ['trenger_avklaring', 'Trenger avklaring'], ['maa_bygges', 'Må bygges']]} aktiv={v.sourceCheck} onVelg={(x) => sett({ sourceCheck: x })} />
          </div>
          <div className="h-felt">
            <span id="li-v-baseline" className="h-etikett">
              Baseline
            </span>
            <Radiogruppe etikettId="li-v-baseline" valg={[['ja', 'Målt'], ['maa_maales', 'Må måles']]} aktiv={v.baselineCheck} onVelg={(x) => sett({ baselineCheck: x })} />
          </div>
          {sjekkGrunn ? (
            <span className="h-felt-grunn" id="li-v-sjekk-grunn">
              <Ikon navn="warning-outline" className="h-varsel-ikon-warning" />
              <span>{sjekkGrunn}</span>
            </span>
          ) : null}
        </div>
      ) : null}
      <Svar id="li-v-svar" verdi={v.reply} onEndre={(t) => sett({ reply: t })} feilFor={feilFor} />
    </>
  );
}

function StegValgt({ v, sett, beslutningstakere, eiere, feilFor, iDag }: { v: Historikk['valgt']; sett: (p: Partial<Historikk['valgt']>) => void; beslutningstakere: Person[]; eiere: Person[]; feilFor: FeilFor; iDag: string }) {
  return (
    <>
      <div className="h-felter-rad">
        <Velg id="li-va-beslutter" etikett="Beslutningstaker" verdi={v.deciderId} folk={beslutningstakere} onVelg={(id) => sett({ deciderId: id })} feilFor={feilFor} />
        <Velg id="li-va-eier" etikett="Eier av testen" verdi={v.ownerId} folk={eiere} onVelg={(id) => sett({ ownerId: id })} feilFor={feilFor} />
        <Dato id="li-va-dato" etikett="Valgt den" verdi={v.on} onEndre={(d) => sett({ on: d })} feilFor={feilFor} iDag={iDag} />
      </div>
      <p className="hb-dempet">Eierskapet regnes som tatt samme dag.</p>
      <Svar id="li-va-svar" verdi={v.reply} onEndre={(t) => sett({ reply: t })} feilFor={feilFor} />
    </>
  );
}

function StegStartet({ v, sett, feilFor, iDag }: { v: Historikk['startet']; sett: (p: Partial<Historikk['startet']>) => void; feilFor: FeilFor; iDag: string }) {
  return (
    <>
      <div className="h-felter-rad">
        <Dato id="li-s-dato" etikett="Startet den" verdi={v.on} onEndre={(d) => sett({ on: d })} feilFor={feilFor} iDag={iDag} />
        <Dato id="li-s-innen" etikett="Innen" verdi={v.testEndsOn} onEndre={(d) => sett({ testEndsOn: d })} feilFor={feilFor} />
      </div>
      {v.testEndsOn ? <p className="hb-dempet">{`Testen skulle gå til ${kortDato(v.testEndsOn)}.`}</p> : null}
      <Tekst id="li-s-ikke" etikett="Det vi ikke gjør · valgfritt" verdi={v.notDoing} onEndre={(t) => sett({ notDoing: t })} feilFor={feilFor} maks={500} />
      <Svar id="li-s-svar" verdi={v.reply} onEndre={(t) => sett({ reply: t })} feilFor={feilFor} />
    </>
  );
}

function StegKonkludert({ v, sett, feilFor, iDag }: { v: Historikk['konkludert']; sett: (p: Partial<Historikk['konkludert']>) => void; feilFor: FeilFor; iDag: string }) {
  const utfallGrunn = feilFor('li-k-utfall');
  const resultatGrunn = feilFor('li-k-resultat');
  return (
    <>
      <div className="h-felter-rad">
        <Dato id="li-k-dato" etikett="Konkludert den" verdi={v.on} onEndre={(d) => sett({ on: d })} feilFor={feilFor} iDag={iDag} />
        <LiFelt id="li-k-resultat" etikett="Resultatet" grunn={resultatGrunn}>
          <input id="li-k-resultat" className="h-input h-input-tall" inputMode="decimal" value={v.resultValue} {...feltAttr('li-k-resultat', resultatGrunn)} onChange={(e) => sett({ resultValue: e.target.value })} />
        </LiFelt>
        <Dato id="li-k-maalt" etikett="Målt den" verdi={v.resultMeasuredOn} onEndre={(d) => sett({ resultMeasuredOn: d })} feilFor={feilFor} iDag={iDag} />
      </div>
      <div className="h-felt">
        <span id="li-k-utfall-h" className="h-etikett">
          Utfall
        </span>
        <Radiogruppe etikettId="li-k-utfall-h" valg={[['bekreftet', 'Bekreftet'], ['delvis', 'Delvis'], ['avkreftet', 'Avkreftet']]} aktiv={v.outcome} onVelg={(x) => sett({ outcome: x })} />
        {utfallGrunn ? (
          <span className="h-felt-grunn">
            <Ikon navn="warning-outline" className="h-varsel-ikon-warning" />
            <span>{utfallGrunn}</span>
          </span>
        ) : null}
      </div>
      <Tekst id="li-k-laerte" etikett="Hva vi lærte" verdi={v.learned} onEndre={(t) => sett({ learned: t })} feilFor={feilFor} rader={4} maks={1000} />
      <Tekst id="li-k-funn" etikett="Åpne funn" verdi={v.openBlocking} onEndre={(t) => sett({ openBlocking: t })} feilFor={feilFor} maks={1000} />
      <Tekst id="li-k-data" etikett="Begrensninger i data" verdi={v.dataLimitations} onEndre={(t) => sett({ dataLimitations: t })} feilFor={feilFor} maks={1000} />
      <Tekst id="li-k-neste" etikett="Neste steg · valgfritt" verdi={v.nextSteps} onEndre={(t) => sett({ nextSteps: t })} feilFor={feilFor} rader={2} maks={500} />
      {/* H19: uenigheten er alltid et synlig felt. */}
      <Tekst id="li-k-uenighet" etikett="Uenighet · valgfritt" verdi={v.dissent} onEndre={(t) => sett({ dissent: t })} feilFor={feilFor} rader={2} maks={1000} />
      <Svar id="li-k-svar" verdi={v.reply} onEndre={(t) => sett({ reply: t })} feilFor={feilFor} />
    </>
  );
}
